import { useEffect, useState, useCallback } from 'react';
import {
    Upload, CheckCircle2, AlertCircle, Loader2, Clock, XCircle, Eye,
    RefreshCw, Activity, Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { ordersApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * SonyUploadPanel — Phase 1 + Phase 2 UI for the "Upload masters to Sony" flow.
 *
 * Admin-only. Placed inside the Master section (per user spec — the
 * action uploads master files, not PO metadata).
 *
 * Behaviour:
 *  • On mount, fetches any existing job. If one exists we render its
 *    status + current execution step + timestamped history + retry
 *    controls (only when failed or stale) + persistent job JSON.
 *  • If no job exists, the primary button is "Upload masters to Sony".
 *    Clicking opens a modal that first calls the preview endpoint —
 *    this validates the order and shows the exact JSON that will be
 *    persisted. The admin confirms with "Create Upload Job" which
 *    hits POST /sony-upload (idempotent).
 *  • Auto-refreshes every 5s while the job is in a non-terminal
 *    state so the admin sees worker progress + heartbeat drift.
 */
const STATUS_META = {
    ready:       { label: 'Ready',      color: 'bg-slate-100 text-slate-700 border-slate-300',      icon: Clock },
    queued:      { label: 'Queued',     color: 'bg-blue-100 text-blue-700 border-blue-300',         icon: Clock },
    processing:  { label: 'Processing', color: 'bg-amber-100 text-amber-700 border-amber-300',      icon: Loader2 },
    completed:   { label: 'Completed',  color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
    failed:      { label: 'Failed',     color: 'bg-red-100 text-red-700 border-red-300',            icon: XCircle },
    received:    { label: 'Received',   color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
};


const StepMeta = {
    starting_worker:    { label: 'Starting worker',    hint: 'Booting the Sony EC2 machine' },
    creating_project:   { label: 'Creating project',   hint: 'Logging into Sony portal' },
    creating_master:    { label: 'Creating master',    hint: 'Setting up masters in Sony' },
    downloading_files:  { label: 'Downloading files',  hint: 'Pulling masters from S3' },
    uploading_files:    { label: 'Uploading files',    hint: 'Sending masters via Aspera' },
    waiting_for_sony:   { label: 'Waiting for Sony',   hint: 'Sony is validating the masters' },
};


const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.ready;
    const Icon = meta.icon;
    return (
        <Badge className={`${meta.color} border`} data-testid={`sony-job-status-${status}`}>
            <Icon className={`h-3 w-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
            {meta.label}
        </Badge>
    );
};


const fmtTs = (iso) => {
    if (!iso) return '—';
    return String(iso).replace('T', ' ').slice(0, 16);
};


const relSecondsAgo = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.round(s / 60)}m ago`;
        return `${Math.round(s / 3600)}h ago`;
    } catch { return null; }
};


export const SonyUploadPanel = ({ orderId }) => {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [previewJson, setPreviewJson] = useState(null);
    const [previewErrors, setPreviewErrors] = useState([]);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    const [reconcileConfirmOpen, setReconcileConfirmOpen] = useState(false);

    const loadExisting = useCallback(async () => {
        try {
            const res = await ordersApi.getSonyUpload(orderId);
            setJob(res.data);
        } catch (err) {
            if (err?.response?.status === 404) setJob(null);
            else toast.error('Failed to load Sony upload job');
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => { loadExisting(); }, [loadExisting]);

    // Poll while non-terminal. Sony EC2 boot can take several minutes;
    // 5s cadence is cheap enough and gives the admin near-real-time
    // insight without hammering the API.
    useEffect(() => {
        const status = (job?.status || '').toLowerCase();
        if (status !== 'queued' && status !== 'processing') return undefined;
        const t = setInterval(loadExisting, 5000);
        return () => clearInterval(t);
    }, [job?.status, loadExisting]);

    const openPreviewModal = async () => {
        setModalOpen(true);
        setPreviewBusy(true);
        setPreviewErrors([]);
        setPreviewJson(null);
        try {
            const res = await ordersApi.previewSonyUpload(orderId);
            setPreviewJson(res.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const errs = Array.isArray(detail?.errors)
                ? detail.errors
                : [typeof detail === 'string' ? detail : 'Preview failed'];
            setPreviewErrors(errs);
        } finally {
            setPreviewBusy(false);
        }
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            const res = await ordersApi.createSonyUpload(orderId);
            setJob(res.data);
            setModalOpen(false);
            toast.success('Sony upload job queued — worker will pick it up');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const errs = Array.isArray(detail?.errors)
                ? detail.errors
                : [typeof detail === 'string' ? detail : 'Create failed'];
            setPreviewErrors(errs);
        } finally {
            setCreating(false);
        }
    };

    const handleRetry = async () => {
        setRetrying(true);
        try {
            const res = await ordersApi.retrySonyUpload(orderId);
            setJob(res.data);
            toast.success('Job requeued — worker will pick it up');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Retry failed');
        } finally {
            setRetrying(false);
        }
    };

    // Admin-only reconciliation for the specific case where Sony has
    // ALREADY reported the upload as ``upload_completed`` but the
    // FLOW-side final ``status: completed`` request was rejected
    // (409), leaving the job stuck in ``failed`` while the underlying
    // upload actually succeeded. Corrects FLOW's status through the
    // admin reconciliation endpoint ONLY — never retries, never
    // starts the EC2 worker / Chrome, never modifies masters, never
    // uploads to Sony again.
    const handleReconcile = async () => {
        setReconciling(true);
        try {
            const res = await ordersApi.reconcileSonyUploadCompleted(job.id);
            setJob(res.data);
            setReconcileConfirmOpen(false);
            toast.success(
                'Reconciled — job marked completed. No files were re-uploaded and the worker was not invoked.',
                { duration: 9000 },
            );
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string'
                ? detail
                : (detail?.message || 'Reconciliation failed');
            toast.error(msg, { duration: 9000 });
        } finally {
            setReconciling(false);
        }
    };

    if (loading) {
        return (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="sony-upload-panel-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading Sony upload status…
            </div>
        );
    }

    const status = (job?.status || '').toLowerCase();
    const isFailed = status === 'failed';
    const isStale = !!job?.is_stale;
    // Reconciliation surfaces ONLY when the underlying Sony upload
    // has succeeded (workflow_checkpoint == upload_completed) but
    // FLOW's job status is stuck at ``failed``. Anything else must
    // go through Retry so a real re-upload actually happens.
    const canReconcileCompleted =
        isFailed && job?.workflow_checkpoint === 'upload_completed';
    // Retry is the correct action for every other failed/stale state.
    // Hide it in the reconciliation window so admins don't
    // accidentally trigger an unnecessary re-upload after Sony
    // already reported success.
    const canRetry = (isFailed || isStale) && !canReconcileCompleted;
    const stepMeta = job?.current_step ? StepMeta[job.current_step] : null;

    return (
        <>
            <div
                className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3"
                data-testid="sony-upload-panel"
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Upload className="h-4 w-4 text-indigo-600" />
                            Sony Master Upload
                        </h4>
                        <p className="text-xs text-slate-600 mt-0.5">
                            {job
                                ? `Project ${job.project_name} - ${(job.masters || []).length} master${(job.masters || []).length === 1 ? '' : 's'}`
                                : 'Send master files to Sony for validation & production.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {job && <StatusBadge status={status} />}
                        {!job && (
                            <Button
                                size="sm"
                                onClick={openPreviewModal}
                                data-testid="sony-upload-button"
                            >
                                <Upload className="h-4 w-4 mr-1" />
                                Upload masters to Sony
                            </Button>
                        )}
                        {canRetry && (
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={handleRetry}
                                disabled={retrying}
                                data-testid="sony-upload-retry"
                            >
                                {retrying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                                Retry
                            </Button>
                        )}
                        {canReconcileCompleted && (
                            <Button
                                size="sm"
                                onClick={() => setReconcileConfirmOpen(true)}
                                disabled={reconciling}
                                data-testid="sony-upload-reconcile-completed"
                                title="Sony reported the upload as completed but FLOW's final status update was rejected. This button ONLY corrects FLOW's status — it never retries the upload, starts the worker, or contacts Sony."
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                                {reconciling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                                Reconcile completed upload
                            </Button>
                        )}
                        {job && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={openPreviewModal}
                                data-testid="sony-upload-view-json"
                            >
                                <Eye className="h-4 w-4 mr-1" />
                                View JSON
                            </Button>
                        )}
                    </div>
                </div>

                {job && (
                    <>
                        {/* Phase 2 execution telemetry */}
                        <div
                            className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-slate-600 border-t border-indigo-100 pt-2"
                            data-testid="sony-job-telemetry"
                        >
                            <div>
                                <div className="text-slate-400 uppercase tracking-wide">Step</div>
                                <div className="font-medium text-slate-700 flex items-center gap-1">
                                    <Activity className="h-3 w-3" />
                                    {stepMeta ? stepMeta.label : (job.current_step || '—')}
                                </div>
                                {stepMeta && <div className="text-slate-500">{stepMeta.hint}</div>}
                            </div>
                            <div>
                                <div className="text-slate-400 uppercase tracking-wide">Created</div>
                                <div className="font-medium text-slate-700">{fmtTs(job.created_at)}</div>
                            </div>
                            <div>
                                <div className="text-slate-400 uppercase tracking-wide">Started</div>
                                <div className="font-medium text-slate-700">{fmtTs(job.processing_started_at)}</div>
                            </div>
                            <div>
                                <div className="text-slate-400 uppercase tracking-wide">Last heartbeat</div>
                                <div className={`font-medium ${isStale ? 'text-red-600' : 'text-slate-700'}`}>
                                    {job.last_heartbeat_at
                                        ? `${fmtTs(job.last_heartbeat_at)} (${relSecondsAgo(job.last_heartbeat_at)})`
                                        : '—'}
                                </div>
                            </div>
                        </div>

                        {job.attempt > 1 && (
                            <div className="text-[11px] text-slate-600 flex items-center gap-1" data-testid="sony-job-attempt">
                                <Zap className="h-3 w-3" />
                                Attempt {job.attempt}
                            </div>
                        )}

                        {isStale && (
                            <div
                                className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 flex items-start gap-2"
                                data-testid="sony-job-stale-warning"
                            >
                                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                Worker hasn't heartbeated in a while — the EC2 instance may have crashed. Retry to requeue the same job.
                            </div>
                        )}

                        {job.error_message && (
                            <div
                                className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 space-y-0.5"
                                data-testid="sony-job-error"
                            >
                                <div className="flex items-center gap-1 font-medium">
                                    <AlertCircle className="h-4 w-4" />
                                    Worker error{job.error_code ? ` (${job.error_code})` : ''}
                                </div>
                                <div className="ml-5 whitespace-pre-wrap">{job.error_message}</div>
                            </div>
                        )}

                        <div className="text-xs text-slate-600 space-y-1" data-testid="sony-job-masters-summary">
                            {(job.masters || []).map((m) => (
                                <div key={m.slot_id || m.disc_number} className="flex items-center justify-between gap-2 border-t border-indigo-100 pt-1 first:border-0 first:pt-0">
                                    <span className="font-mono">
                                        Disc {m.disc_number}: {m.catalogue_number} ({m.media_type}) - {(m.files || []).length} file{(m.files || []).length === 1 ? '' : 's'}
                                    </span>
                                    <StatusBadge status={m.status || 'ready'} />
                                </div>
                            ))}
                        </div>

                        {(job.status_history || []).length > 0 && (
                            <details className="text-xs text-slate-600" data-testid="sony-job-history">
                                <summary className="cursor-pointer hover:text-slate-800">
                                    Status history ({(job.status_history || []).length})
                                </summary>
                                <div className="mt-2 space-y-1 border-l-2 border-indigo-200 pl-3">
                                    {(job.status_history || []).map((h, i) => (
                                        <div key={i} className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-mono text-[10px] text-slate-500">{fmtTs(h.at)}</span>
                                            <span className="font-medium">{h.status}</span>
                                            {h.manual && (
                                                <span
                                                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                                                    data-testid={`sony-job-history-manual-${i}`}
                                                    title="Admin recovery — Sony upload was finished outside FLOW"
                                                >
                                                    Manual
                                                </span>
                                            )}
                                            {h.actor_name && <span className="text-slate-500">- {h.actor_name}</span>}
                                            {h.note && <span className="text-slate-500">- {h.note}</span>}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}
                    </>
                )}
            </div>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent
                    className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
                    data-testid="sony-upload-modal"
                >
                    <DialogHeader>
                        <DialogTitle>{job ? 'Sony Upload Job JSON' : 'Preview Sony upload job'}</DialogTitle>
                        <DialogDescription>
                            {job
                                ? 'This is the exact JSON persisted for the EC2 upload worker.'
                                : 'Review the exact JSON that will be sent to Sony. Nothing is uploaded yet.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-auto space-y-3">
                        {previewBusy && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Validating & building…
                            </div>
                        )}

                        {previewErrors.length > 0 && (
                            <div
                                className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm space-y-1"
                                data-testid="sony-upload-errors"
                            >
                                <div className="flex items-center gap-2 font-medium text-red-800">
                                    <AlertCircle className="h-4 w-4" />
                                    {previewErrors.length} validation problem{previewErrors.length === 1 ? '' : 's'}:
                                </div>
                                <ul className="ml-6 list-disc text-red-700">
                                    {previewErrors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}

                        {(job || previewJson) && (
                            <pre
                                className="rounded-lg border border-slate-200 bg-slate-900 text-slate-100 text-xs p-3 overflow-auto max-h-[50vh]"
                                data-testid="sony-upload-json"
                            >
{JSON.stringify(job || previewJson, null, 2)}
                            </pre>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>Close</Button>
                        {!job && previewJson && previewErrors.length === 0 && (
                            <Button
                                onClick={handleCreate}
                                disabled={creating}
                                data-testid="sony-upload-confirm-create"
                            >
                                {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                                Queue upload job
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Reconciliation confirmation dialog ─────────────
                Admin-only correction of FLOW's status when Sony has
                already reported the upload as ``upload_completed``
                but the FLOW-side final ``status: completed`` request
                was rejected (409). NEVER retries the job, starts the
                EC2 worker / Chrome, modifies masters, or uploads to
                Sony again. */}
            <Dialog open={reconcileConfirmOpen} onOpenChange={setReconcileConfirmOpen}>
                <DialogContent className="max-w-lg" data-testid="sony-reconcile-dialog">
                    <DialogHeader>
                        <DialogTitle>Reconcile FLOW status only</DialogTitle>
                        <DialogDescription>
                            Sony has already reported the upload for job{' '}
                            <code className="font-mono">{job?.id}</code> as{' '}
                            <code className="font-mono">upload_completed</code>.
                            The final FLOW-side status update was rejected, leaving
                            this job stuck as <strong>failed</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-sm space-y-3">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 text-xs space-y-1">
                            <div className="font-medium">This will:</div>
                            <ul className="list-disc ml-5">
                                <li>Flip <code>status</code> from <code>failed</code> to <code>completed</code>.</li>
                                <li>Advance every <code>upload_units[]</code> entry to <code>upload_completed</code>.</li>
                                <li>Clear the transient <code>error_message</code>.</li>
                                <li>Append an audit-trail history entry naming you.</li>
                            </ul>
                        </div>
                        <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-slate-700 text-xs space-y-1">
                            <div className="font-medium text-slate-900">This will NOT:</div>
                            <ul className="list-disc ml-5">
                                <li>Retry the upload or re-queue the job.</li>
                                <li>Start the EC2 worker or open Chrome.</li>
                                <li>Modify any master files.</li>
                                <li>Contact Sony or re-upload anything.</li>
                            </ul>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setReconcileConfirmOpen(false)}
                            disabled={reconciling}
                            data-testid="sony-reconcile-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReconcile}
                            disabled={reconciling}
                            data-testid="sony-reconcile-confirm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {reconciling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                            Reconcile FLOW status
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SonyUploadPanel;
